import { c as _c } from "react/compiler-runtime";
import CompareItem from "./CompareItem";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { useCompare } from "./useCompare";
import { Card, CardContent } from "@/components/ui/card";
const CompareChart = dynamic(() => import("./CompareChart").then(m => ({
  default: m.CompareChart
})), {
  ssr: false,
  loading: () => null
});
type Props = {
  slug: string;
  originalBusinessType?: string;
  originalListingType?: string;
  originalPrice: number;
  originalArea: number;
};
const globalScrollbarStyles = `
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
`;
export default function Compare(t0) {
  const $ = _c(38);
  const {
    slug,
    originalBusinessType,
    originalListingType,
    originalPrice,
    originalArea
  } = t0;
  const t = useTranslations("modules.realEstateDetail");
  const {
    shouldRender,
    toRender,
    selectedItem,
    setSelectedItem,
    scrollContainerRef,
    scrollTo,
    showLeftButton,
    showRightButton
  } = useCompare(slug, originalBusinessType, originalListingType);
  if (!shouldRender) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <style dangerouslySetInnerHTML={{
      __html: globalScrollbarStyles
    }} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t("compareTitle");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className="text text-foreground-heading font-semibold">{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== scrollTo || $[6] !== selectedItem || $[7] !== setSelectedItem || $[8] !== showLeftButton || $[9] !== showRightButton || $[10] !== t) {
    t4 = selectedItem ? <button onClick={() => setSelectedItem(null)} className="p-2 rounded-full border border-border-strong hover:bg-muted" aria-label={t("closeCompare")}><X className="w-4 h-4" /></button> : <div className="flex space-x-2"><button aria-label={t("scrollPrevious")} onClick={() => scrollTo("left")} className={`p-2 rounded-full border border-border-strong ${!showLeftButton ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"}`} disabled={!showLeftButton}><ChevronLeft className="w-4 h-4" /></button><button aria-label={t("scrollNext")} onClick={() => scrollTo("right")} className={`p-2 rounded-full border border-border-strong ${!showRightButton ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"}`} disabled={!showRightButton}><ChevronRight className="w-4 h-4" /></button></div>;
    $[5] = scrollTo;
    $[6] = selectedItem;
    $[7] = setSelectedItem;
    $[8] = showLeftButton;
    $[9] = showRightButton;
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <div className="flex items-center justify-between mb-2">{t3}{t4}</div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      WebkitOverflowScrolling: "touch"
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== originalPrice || $[17] !== selectedItem || $[18] !== setSelectedItem || $[19] !== toRender) {
    let t8;
    if ($[21] !== originalPrice || $[22] !== selectedItem || $[23] !== setSelectedItem) {
      t8 = id => <motion.div key={id} layout={true} initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.8
      }} transition={{
        duration: 0.3
      }} className="w-1/3 shrink-0" onClick={() => setSelectedItem(prev => prev === id ? null : id)}><CompareItem slug={id} originalPrice={originalPrice} selectedItem={!!selectedItem} /></motion.div>;
      $[21] = originalPrice;
      $[22] = selectedItem;
      $[23] = setSelectedItem;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    t7 = toRender.map(t8);
    $[16] = originalPrice;
    $[17] = selectedItem;
    $[18] = setSelectedItem;
    $[19] = toRender;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[25] !== originalArea || $[26] !== originalPrice || $[27] !== selectedItem) {
    t8 = selectedItem && <motion.div layout={true} initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.8
    }} transition={{
      duration: 0.3
    }} className="w-2/3 shrink-0"><CompareChart originalPrice={originalPrice} originalArea={originalArea} slug={selectedItem} /></motion.div>;
    $[25] = originalArea;
    $[26] = originalPrice;
    $[27] = selectedItem;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t7 || $[30] !== t8) {
    t9 = <AnimatePresence initial={false} data-sentry-element="AnimatePresence" data-sentry-source-file="Compare.tsx">{t7}{t8}</AnimatePresence>;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== scrollContainerRef || $[33] !== t9) {
    t10 = <div ref={scrollContainerRef} className="flex overflow-x-auto no-scrollbar gap-2 pb-2 snap-x" style={t6}>{t9}</div>;
    $[32] = scrollContainerRef;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t5) {
    t11 = <Card data-sentry-element="Card" data-sentry-component="Compare" data-sentry-source-file="Compare.tsx"><CardContent data-sentry-element="CardContent" data-sentry-source-file="Compare.tsx">{t1}<div className="overflow-x-auto">{t5}{t10}</div></CardContent></Card>;
    $[35] = t10;
    $[36] = t5;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  return t11;
}